import type { ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'
import {
  ActionMenu,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
  cn,
  type ActionMenuProps,
} from '@atrvd/saas-ui'
import { FACILITY_SCOPE_COPY } from './financialAccountsScope'

interface ReadOnlyTooltipProps {
  /** The page is an inactive facility's: explain why `children` is disabled. */
  readOnly: boolean
  /** The control, already disabled by the caller when `readOnly`. */
  children: ReactNode
}

/**
 * Explains a control disabled on an inactive facility's page with the
 * "Not available for an inactive facility" tooltip. A disabled button fires
 * no pointer or focus events, so the tooltip hangs on a focusable wrapper.
 * Renders `children` untouched when not read-only.
 */
export function ReadOnlyTooltip({ readOnly, children }: ReadOnlyTooltipProps) {
  if (!readOnly) return <>{children}</>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>{FACILITY_SCOPE_COPY.readOnlyTooltip}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}

interface ReadOnlyActionMenuProps extends ActionMenuProps {
  readOnly: boolean
}

/**
 * A kebab `ActionMenu`, or — on an inactive facility's page — its trigger
 * disabled with the read-only tooltip. `ActionMenu` has no disabled state of
 * its own, so the disabled trigger is drawn here with the same look and name.
 */
export function ReadOnlyActionMenu({ readOnly, ...menu }: ReadOnlyActionMenuProps) {
  if (!readOnly) return <ActionMenu {...menu} />
  return (
    <ReadOnlyTooltip readOnly>
      <button
        type="button"
        disabled
        aria-label={menu.ariaLabel ?? 'Actions'}
        className={cn(
          'inline-flex h-8 w-8 cursor-not-allowed items-center justify-center rounded-lg text-muted-foreground opacity-50',
          menu.className,
        )}
      >
        <MoreVertical aria-hidden="true" className="h-4 w-4" />
      </button>
    </ReadOnlyTooltip>
  )
}
